import React, { useState } from "react";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import { Ticket, Eye, EyeOff } from "lucide-react";
import { registerWithCode } from "@/lib/api";

// Standalone invite-code signup at /beta. On success it drops the user's JWT
// cookies (via the register endpoint) and sends them into the main app.
export default function BetaSignup() {
    const [displayName, setDisplayName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [code, setCode] = useState(() => {
        try { return (new URLSearchParams(window.location.search).get("code") || "").toUpperCase(); }
        catch { return ""; }
    });
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email || !password) {
            setError("fill in your email and password 🍡");
            return;
        }
        setBusy(true);
        try {
            await registerWithCode(email.trim().toLowerCase(), password, displayName.trim(), code.trim() || null);
            toast.success("welcome to the beta 🍡 taking you in…");
            setTimeout(() => { window.location.href = "/"; }, 700);
        } catch (err) {
            const msg = err?.response?.data?.detail || "something went sideways — try again in a sec?";
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen font-body flex items-center justify-center px-5 py-12" data-testid="beta-signup">
            <Toaster position="top-center" richColors={false} />
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 220, damping: 22 }}
                className="w-full max-w-md"
            >
                <div className="text-center mb-6">
                    <div className="text-5xl mb-3">🍡</div>
                    <h1 className="font-display text-3xl text-warm-900">Join the Mochi beta</h1>
                    <p className="text-warm-500 text-sm mt-2">
                        got an invite code? drop it below. no code? if your email's on the list, you're still in 🌸
                    </p>
                </div>

                <form
                    onSubmit={submit}
                    className="bg-cream rounded-clay shadow-clay border border-warm-300/30 p-6 space-y-4"
                >
                    <div>
                        <label className="block text-warm-800 text-sm font-semibold mb-1.5">Email</label>
                        <input
                            type="email" value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            autoComplete="email" inputMode="email"
                            className="w-full px-4 py-3 rounded-2xl bg-surface border border-warm-300/40 text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40"
                            data-testid="beta-email-input"
                        />
                    </div>
                    <div>
                        <label className="block text-warm-800 text-sm font-semibold mb-1.5">
                            Name <span className="text-warm-500 font-normal">(what mochi calls you — optional)</span>
                        </label>
                        <input
                            type="text" value={displayName} maxLength={40}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="mochifan"
                            autoComplete="nickname"
                            className="w-full px-4 py-3 rounded-2xl bg-surface border border-warm-300/40 text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40"
                            data-testid="beta-display-name-input"
                        />
                    </div>
                    <div>
                        <label className="block text-warm-800 text-sm font-semibold mb-1.5">Password</label>
                        <div className="relative">
                            <input
                                type={showPw ? "text" : "password"} value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="at least 6 characters"
                                className="w-full px-4 py-3 pr-11 rounded-2xl bg-surface border border-warm-300/40 text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40"
                                data-testid="beta-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-warm-500"
                                aria-label={showPw ? "Hide password" : "Show password"}
                                data-testid="beta-password-toggle"
                            >
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-warm-800 text-sm font-semibold mb-1.5 flex items-center gap-1.5">
                            <Ticket className="w-3.5 h-3.5 text-terracotta" /> Invite code
                            <span className="text-warm-500 font-normal">(optional)</span>
                        </label>
                        <input
                            type="text" value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="MOCHI-XXXXXX"
                            className="w-full px-4 py-3 rounded-2xl bg-surface border border-warm-300/40 text-warm-900 tracking-wider focus:outline-none focus:ring-2 focus:ring-terracotta/40"
                            data-testid="beta-code-input"
                        />
                    </div>

                    {error && (
                        <p className="text-terracotta text-sm text-center" data-testid="beta-error">{error}</p>
                    )}

                    <button
                        type="submit"
                        disabled={busy}
                        className="w-full py-3 rounded-full text-cream font-semibold bg-terracotta hover:bg-terracotta-hover transition-colors disabled:opacity-60"
                        data-testid="beta-submit-btn"
                    >
                        {busy ? "letting you in…" : "Claim my Mochi 🍡"}
                    </button>

                    <p className="text-center text-warm-500 text-sm">
                        already have an account?{" "}
                        <a href="/" className="text-terracotta font-semibold hover:underline" data-testid="beta-signin-link">
                            sign in
                        </a>
                    </p>
                </form>
            </motion.div>
        </div>
    );
}
